import { gql, useMutation, useQuery } from '@provision/graphql-client';
import { useEffect, useRef } from 'react';

const BULK_UPDATE_ENTITIES_MUTATION = gql`
  mutation BulkUpdateEntities($input: BulkUpdateEntitiesInput!) {
    bulkUpdateEntities(input: $input) {
      jobId
      status
      errorCode
      total
      processed
      updatedCount
      failedCount
      idempotentReplay
      undoUntil
    }
  }
`;

const BULK_UPDATE_ENTITIES_PROGRESS_QUERY = gql`
  query BulkUpdateEntitiesProgress($input: BulkUpdateEntitiesProgressInput!) {
    bulkUpdateEntitiesProgress(input: $input) {
      jobId
      status
      errorCode
      total
      processed
      updatedCount
      failedCount
      errors {
        message
        records {
          id
          label
        }
      }
      createdAt
      updatedAt
      submittedAt
      undoUntil
      undoUsedAt
      undoConflictCount
      undoIneligibilityCode
      undoIneligibilityMessage
      entityName
      operation {
        attribute
      }
    }
  }
`;

const UNDO_BULK_UPDATE_ENTITIES_MUTATION = gql`
  mutation UndoBulkUpdateEntities($input: UndoBulkUpdateEntitiesInput!) {
    undoBulkUpdateEntities(input: $input) {
      jobId
      status
      errorCode
      updatedCount
      failedCount
      undoUsedAt
      undoIneligibilityCode
      undoIneligibilityMessage
    }
  }
`;

export interface BulkUpdateSelectionInput {
  mode: 'manual';
  manual: {
    selectedIds: string[];
  };
}

export interface BulkUpdateEntitiesInput {
  partnerId: number;
  entityName: string;
  idempotencyKey: string;
  selection: BulkUpdateSelectionInput;
  operation: {
    attribute: string;
    value?: string;
    metadata?: string;
  };
}

export interface BulkUpdateErrorRecord {
  id: string;
  label?: string | null;
}

export interface BulkUpdateError {
  message: string;
  records?: BulkUpdateErrorRecord[] | null;
}

export interface BulkUpdateOperationSummary {
  attribute: string;
}

export interface BulkUpdateEntitiesProgress {
  jobId: string;
  status: string;
  errorCode: string;
  total: number;
  processed: number;
  updatedCount: number;
  failedCount: number;
  errors: BulkUpdateError[];
  createdAt: string;
  updatedAt: string;
  submittedAt: string;
  undoUntil?: string | null;
  undoUsedAt?: string | null;
  undoConflictCount: number;
  undoIneligibilityCode?: string | null;
  undoIneligibilityMessage?: string | null;
  entityName: string;
  operation: BulkUpdateOperationSummary;
}

interface BulkUpdateEntitiesMutationResult {
  bulkUpdateEntities: {
    jobId: string;
    status: string;
    errorCode: string;
    total: number;
    processed: number;
    updatedCount: number;
    failedCount: number;
    idempotentReplay: boolean;
    undoUntil?: string | null;
  };
}

interface BulkUpdateEntitiesProgressQueryResult {
  bulkUpdateEntitiesProgress: BulkUpdateEntitiesProgress;
}

interface UndoBulkUpdateEntitiesMutationResult {
  undoBulkUpdateEntities: {
    jobId: string;
    status: string;
    errorCode: string;
    updatedCount: number;
    failedCount: number;
    undoUsedAt?: string | null;
    undoIneligibilityCode?: string | null;
    undoIneligibilityMessage?: string | null;
  };
}

export const useBulkUpdateEntitiesMutation = () => {
  return useMutation<BulkUpdateEntitiesMutationResult, { input: BulkUpdateEntitiesInput }>(
    BULK_UPDATE_ENTITIES_MUTATION,
  );
};

export const useBulkUpdateEntitiesProgressQuery = (
  jobId?: string | null,
  pollInterval = 0,
) => {
  const latestStatusRef = useRef<string | undefined>(undefined);

  const query = useQuery<
    BulkUpdateEntitiesProgressQueryResult,
    {
      input: {
        jobId: string;
      };
    }
  >(BULK_UPDATE_ENTITIES_PROGRESS_QUERY, {
    skip: !jobId,
    fetchPolicy: 'network-only',
    variables: jobId ? { input: { jobId } } : undefined,
    pollInterval,
    onCompleted: (data) => {
      latestStatusRef.current = data?.bulkUpdateEntitiesProgress?.status;
    },
    skipPollAttempt: () => {
      return isProgressTerminal(latestStatusRef.current);
    },
    context: { ttl: 0 },
  });

  useEffect(() => {
    latestStatusRef.current = query.data?.bulkUpdateEntitiesProgress?.status;
  }, [query.data?.bulkUpdateEntitiesProgress?.status]);

  return query;
};

export function isProgressTerminal(status: string | undefined): boolean {
  return Boolean(
    status &&
      ['done', 'partial_failure', 'failed', 'undone'].includes(String(status).toLowerCase()),
  );
}

export const useUndoBulkUpdateEntitiesMutation = () => {
  return useMutation<UndoBulkUpdateEntitiesMutationResult, { input: { jobId: string } }>(
    UNDO_BULK_UPDATE_ENTITIES_MUTATION,
  );
};
